import { api } from './api'

export interface ToolItem {
  id: string
  name: string
  chatbot_id?: string  // The chatbot this tool belongs to
  description?: string
  use_when?: string
  type: 'http' | 'mcp' | 'internal' | 'system'
  base_url?: string
  path_template?: string
  method?: string
  headers_template?: Record<string, string>
  query_schema?: Record<string, any>
  body_schema?: Record<string, any>
  base_url_allowlist?: string[]
  mcp_config?: {
    url?: string
    transport?: 'http' | 'stdio'
    tool_prefix?: string
    command?: string
    args?: string[]
    env?: Record<string, string>
    timeout?: number
    enabled_tool_names?: string[]  // List of selected tools from MCP server
  }
  enabled_tool_names?: string[]  // List of selected tools from MCP server
  skill_id?: string | null  // Set when tool is auto-created by a skill
  is_active: boolean
  is_system?: boolean  // True for built-in tools (KB search) - cannot be deleted/edited
  enabled?: boolean  // Whether the tool is enabled for the bot
}

export interface MCPServerTool {
  name: string
  description?: string
  input_schema?: Record<string, any>
}

export interface MCPServerToolsResponse {
  tools: MCPServerTool[]
  total_count: number
}

export interface CreateHttpToolRequest {
  name: string
  chatbot_id: string  // Required: the chatbot this tool belongs to
  description?: string
  use_when?: string
  base_url: string
  path_template: string
  method?: string
  headers_template?: Record<string, string>
  base_url_allowlist?: string[]
  body_schema?: Record<string, any>
  query_schema?: Record<string, any>
}

export interface UpdateHttpToolRequest {
  name?: string
  chatbot_id?: string
  description?: string
  use_when?: string
  base_url?: string
  path_template?: string
  method?: string
  headers_template?: Record<string, string>
  base_url_allowlist?: string[]
  body_schema?: Record<string, any>
  query_schema?: Record<string, any>
  response_schema?: Record<string, any>
}

export interface CreateMcpToolRequest {
  name: string
  chatbot_id: string  // Required: the chatbot this tool belongs to
  description?: string
  use_when?: string
  mcp_config: {
    url?: string
    transport?: 'http' | 'stdio'
    tool_prefix?: string
    command?: string
    args?: string[]
    env?: Record<string, string>
    timeout?: number
    enabled_tool_names?: string[]  // Optional: filter specific tools
  }
  enabled_tool_names?: string[]  // Optional: filter specific tools (top-level for API)
}

export interface UpdateMcpToolRequest {
  name?: string
  chatbot_id?: string
  description?: string
  use_when?: string
  mcp_config?: {
    url?: string
    transport?: 'http' | 'stdio'
    tool_prefix?: string
    command?: string
    args?: string[]
    env?: Record<string, string>
    timeout?: number
    enabled_tool_names?: string[]
  }
  enabled_tool_names?: string[]
}

export const toolsService = {
  async listTools(chatbotId?: string): Promise<ToolItem[]> {
    const params = chatbotId ? { chatbot_id: chatbotId } : {}
    const { data } = await api.get<ToolItem[]>('/v1/tools', { params })
    return data
  },

  async createHttpTool(body: CreateHttpToolRequest): Promise<ToolItem> {
    const { data } = await api.post<ToolItem>('/v1/tools', body)
    return data
  },

  async createMcpTool(body: CreateMcpToolRequest): Promise<ToolItem> {
    const { data } = await api.post<ToolItem>('/v1/tools/mcp', body)
    return data
  },

  async listBotTools(botId: string): Promise<ToolItem[]> {
    const { data } = await api.get<ToolItem[]>(`/v1/chatbots/${botId}/tools`)
    return data
  },

  async setBotToolEnabled(botId: string, toolId: string, enabled: boolean): Promise<void> {
    await api.post('/v1/bot-tools', { bot_id: botId, tool_id: toolId, enabled })
  },

  async updateHttpTool(toolId: string, body: UpdateHttpToolRequest): Promise<ToolItem> {
    const { data } = await api.put<ToolItem>(`/v1/tools/${toolId}`, body)
    return data
  },

  async updateMcpTool(toolId: string, body: UpdateMcpToolRequest): Promise<ToolItem> {
    const { data} = await api.put<ToolItem>(`/v1/tools/${toolId}`, body)
    return data
  },

  /**
   * List available tools from an MCP server before creating an integration
   */
  async listMcpServerTools(mcpConfig: {
    url?: string
    transport?: 'http' | 'stdio'
    command?: string
    args?: string[]
    env?: Record<string, string>
  }): Promise<MCPServerToolsResponse> {
    const { data } = await api.post<MCPServerToolsResponse>('/v1/tools/mcp/list-available', {
      mcp_config: mcpConfig
    })
    return data
  },

  /**
   * Delete a tool
   */
  async deleteTool(toolId: string): Promise<void> {
    await api.delete(`/v1/tools/${toolId}`)
  },

  /**
   * Get tool execution stats and recent call logs for a chatbot
   */
  async getToolStats(botId: string): Promise<ToolStatsResponse> {
    const { data } = await api.get<ToolStatsResponse>(`/v1/chatbots/${botId}/tool-stats`)
    return data
  },
}

export interface RecentCall {
  id: string
  status: string
  latency_ms?: number
  error?: string
  args?: Record<string, any>
  created_at?: string
}

export interface ToolStatItem {
  tool_id: string
  tool_name: string
  total_calls: number
  success_count: number
  error_count: number
  avg_latency_ms?: number
  last_used?: string
  recent_calls: RecentCall[]
}

export interface ToolStatsResponse {
  tools: ToolStatItem[]
}
